import { makeApi, Zodios } from '@zodios/core'
import { z } from 'zod'

const devIdentityApi = makeApi([
  {
    method: 'post',
    path: '/dev/identity/from-jwt',
    alias: 'devIdentityFromJwt',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z
          .object({
            jwt: z.string(),
            auth_user_id: z.number().int().optional(),
          })
          .strict(),
      },
    ],
    response: z.unknown(),
  },
])

const createClient = (baseUrl: string) =>
  // Intentionally separate from portalApiClient so we don't accidentally recurse
  // into the portalApiClient request interceptor when fetching the dev identity.
  new Zodios(baseUrl, devIdentityApi, {
    axiosConfig: {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    },
  })

/**
 * Dev-only helper to convert a Stytch session JWT into the JSON payload we send
 * via the `x-user-identity` header when talking to a local Portal API.
 *
 * This is only used when the Portal API base URL is localhost.
 */
export const fetchUserIdentityFromJwtDev = async (params: {
  baseUrl: string
  jwt: string
  authUserId?: string | null
}): Promise<string | null> => {
  const { baseUrl, jwt, authUserId } = params

  const authUserIdNum =
    authUserId == null || authUserId === '' ? undefined : Number.parseInt(authUserId, 10)
  if (authUserIdNum != null && Number.isNaN(authUserIdNum)) return null

  const client = createClient(baseUrl)

  const postIdentity = async (body: {
    jwt: string
    auth_user_id?: number
  }): Promise<string | null> => {
    try {
      const identity = await client.post('/dev/identity/from-jwt', body)
      const identityJson = JSON.stringify(identity)
      return identityJson ?? null
    } catch {
      return null
    }
  }

  try {
    const body: { jwt: string; auth_user_id?: number } = { jwt }
    if (authUserIdNum != null) {
      body.auth_user_id = authUserIdNum
    }

    const identityJson = await postIdentity(body)
    if (identityJson != null) return identityJson

    // Fall back to JWT-only lookup in case the caller-supplied auth_user_id is stale.
    if (authUserIdNum != null) {
      return postIdentity({ jwt })
    }

    return null
  } catch {
    return null
  }
}
